/* global google */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Loader } from '@googlemaps/js-api-loader';
import { GOOGLE_MAPS_API_KEY } from '@utils/config';

const GOOGLE_MAPS_STYLES = [
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [{ color: '#ebf1f5' }],
  },
  {
    featureType: 'landscape.natural',
    elementType: 'geometry',
    stylers: [{ color: '#f4f4f4' }],
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [{ color: '#d2edc1' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.icon',
    stylers: [{ color: '#777b7e' }],
  },
  {
    featureType: 'poi',
    elementType: 'labels.text.fill',
    stylers: [{ color: '#777b7e' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#c3dcf3' }],
  },
];

/**
 * Send address info to Google Maps API to get a map for a home.
 *
 * @param {string} address
 */
const initMap = async (address, isMarkerEnabled = true) => {
  if (!address) {
    throw 'Failed to initialize map: missing parameters';
  }
  const loader = new Loader({
    apiKey: GOOGLE_MAPS_API_KEY,
    version: 'weekly',
  });

  await loader.load();

  const geocoder = new google.maps.Geocoder();

  await geocoder.geocode(
    {
      address,
      region: 'US',
    },
    (results, status) => {
      if (status === 'OK') {
        const map = new google.maps.Map(document.getElementById('map'), {
          center: results[0].geometry.location,
          zoom: 11,
          disableDefaultUI: true,
          styles: GOOGLE_MAPS_STYLES,
        });

        const createZoomControl = (controlDiv, map) => {
          controlDiv.style.padding = '30px';

          const controlWrapper = document.createElement('div');
          controlWrapper.style.backgroundColor = 'none';
          controlWrapper.style.borderStyle = 'none';
          controlWrapper.style.borderColor = 'none';
          controlWrapper.style.borderWidth = '0';
          controlWrapper.style.cursor = 'pointer';
          controlWrapper.style.textAlign = 'center';
          controlWrapper.style.width = '32px';
          controlWrapper.style.height = '74px';
          controlDiv.appendChild(controlWrapper);

          const zoomInButton = document.createElement('div');
          zoomInButton.style.width = '32px';
          zoomInButton.style.height = '32px';
          zoomInButton.style.backgroundColor = '#ffffff';
          zoomInButton.style.borderRadius = '50%';
          zoomInButton.style.marginBottom = '10px';
          zoomInButton.style.boxShadow = '0px 4px 16px rgba(0, 0, 0, 0.2)';
          zoomInButton.style.backgroundImage = `url('/public-assets/images/plus-circle-32x32.svg')`;
          controlWrapper.appendChild(zoomInButton);

          const zoomOutButton = document.createElement('div');
          zoomOutButton.style.width = '32px';
          zoomOutButton.style.height = '32px';
          zoomOutButton.style.backgroundColor = '#ffffff';
          zoomOutButton.style.borderRadius = '50%';
          zoomOutButton.style.boxShadow = '0px 4px 16px rgba(0, 0, 0, 0.2)';
          zoomOutButton.style.backgroundImage = `url('/public-assets/images/minus-circle-32x32.svg')`;
          controlWrapper.appendChild(zoomOutButton);

          zoomInButton.addEventListener(
            'click',
            () => {
              map.setZoom(map.getZoom() + 1);
            },
            false
          );

          zoomOutButton.addEventListener(
            'click',
            () => {
              map.setZoom(map.getZoom() - 1);
            },
            false
          );
        };

        const marker = new google.maps.Marker({
          position: results[0].geometry.location,
          map,
          title: 'Home',
          icon: '/public-assets/images/map-marker.png',
        });
        // if there's no exact address, marker is disabled
        marker.setMap(isMarkerEnabled ? map : null);

        let zoomControlDiv = document.createElement('div');

        createZoomControl(zoomControlDiv, map);

        zoomControlDiv.index = 1;

        map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(
          zoomControlDiv
        );
      } else {
        console.log(status);
        // show failure message
      }
    }
  );
};

const GoogleMap = ({ address1, postalCode, city, stateProvince }) => {
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    (async () => {
      if (typeof window === 'undefined') return;

      try {
        await initMap(`${address1} ${postalCode}`);
      } catch (error1) {
        console.error(
          `Failed to initialize map: ${error1} Reverting to fallback map.`
        );

        try {
          await initMap(`${city} ${stateProvince} ${postalCode}`, false);
        } catch (error2) {
          console.error(`Failed to initialize fallback map: ${error2}`);
          setIsError(true);
        }
      }
    })();
  }, []);

  if (isError) {
    return <></>;
  }

  return <div id="map"></div>;
};

GoogleMap.propTypes = {
  address1: PropTypes.any,
  postalCode: PropTypes.any,
  city: PropTypes.string,
  stateProvince: PropTypes.string,
};

export default GoogleMap;
